import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import Loader from 'components/Loader';
import React, { FunctionComponent } from 'react';

interface StudyManifestInProgressDialogProps {
  open: boolean;
  completedBatches: number;
  totalBatches: number;
  onClose: (e: React.MouseEvent<HTMLButtonElement>) => void;
  setRunningDialogOpen: (open: boolean) => void;
  failedBatches?: number;
  failureMessage?: string | null;
  overrideFailed?: boolean;
  failedSchedulingCount?: number;
  onRetryFailedBatches?: () => void;
}

export const StudyManifestInProgressDialog: FunctionComponent<
  React.PropsWithChildren<StudyManifestInProgressDialogProps>
> = ({
  open,
  completedBatches,
  totalBatches,
  onClose,
  setRunningDialogOpen,
  failedBatches = 0,
  failureMessage = null,
  overrideFailed = false,
  failedSchedulingCount = 0,
  onRetryFailedBatches,
}) => {
  const settledBatches = completedBatches + failedBatches;
  const isFinished = overrideFailed || (totalBatches > 0 && settledBatches === totalBatches);
  const hasBatchFailures = failedBatches > 0;
  const hasSchedulingFailures = failedSchedulingCount > 0;
  const progressValue = totalBatches > 0 ? (settledBatches / totalBatches) * 100 : 0;
  const assignmentFailureMessage =
    failureMessage && failureMessage !== 'Failed to create procedures'
      ? failureMessage
      : 'Case assignment was not saved. Review the case names and slides, then try again.';

  return (
    <Dialog open={open} aria-labelledby="case-assignment-progress-title">
      <DialogContent>
        <Box aria-live="polite">
          {!isFinished ? (
            <Loader
              inline
              message="Saving the case assignment."
              value={progressValue}
              ariaLabel="Case assignment progress"
            />
          ) : null}
          {isFinished && !hasBatchFailures && !hasSchedulingFailures && !overrideFailed ? (
            <Typography id="case-assignment-progress-title">Case assignment saved.</Typography>
          ) : null}
          {overrideFailed ? (
            <Typography id="case-assignment-progress-title">
              Existing cases could not be replaced, so this case assignment was not saved.
            </Typography>
          ) : null}
          {isFinished && hasBatchFailures ? (
            <Typography id="case-assignment-progress-title">{assignmentFailureMessage}</Typography>
          ) : null}
          {isFinished && !hasBatchFailures && hasSchedulingFailures ? (
            <Typography id="case-assignment-progress-title">
              Cases were saved, but initial image analysis did not start for {failedSchedulingCount}{' '}
              {failedSchedulingCount === 1 ? 'slide' : 'slides'}. Close this dialog, then run initial image analysis
              again for those slides.
            </Typography>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        {isFinished && hasBatchFailures && onRetryFailedBatches ? (
          <Button variant="outlined" color="inherit" onClick={onRetryFailedBatches}>
            Try again
          </Button>
        ) : null}
        <Button
          variant="contained"
          color={isFinished && !hasBatchFailures && !hasSchedulingFailures && !overrideFailed ? 'primary' : 'inherit'}
          disabled={!isFinished}
          onClick={(e) => {
            setRunningDialogOpen(false);
            onClose(e);
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
